<script setup lang="ts">
import type { NotificationData } from '@/services/notification/models/notification';
import { notificationService } from '@/services/notification/notification-service';
import {
  PhBell,
  PhCheckCircle,
  PhInfo,
  PhLink,
  PhWarning,
  PhXCircle,
} from '@phosphor-icons/vue';
import { computed, type Component } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const notifications = computed(() => notificationService.notifications.value);

const typeIconMap: Record<string, Component> = {
  danger: PhXCircle,
  warning: PhWarning,
  success: PhCheckCircle,
  info: PhInfo,
  primary: PhBell,
  link: PhLink,
};

function notificationClass(notification: NotificationData) {
  if (!notification.type) {
    return undefined;
  }

  return [`is-${notification.type}`];
}

function notificationIcon(notification: NotificationData): Component {
  return typeIconMap[notification.type ?? ''] ?? PhBell;
}

// Interrupt the screen reader for danger/warning, announce politely otherwise.
function notificationRole(notification: NotificationData): 'alert' | 'status' {
  return notification.type === 'danger' || notification.type === 'warning'
    ? 'alert'
    : 'status';
}
</script>

<template>
  <div class="notifications">
    <div
      v-for="(notification, index) in notifications"
      :key="notification.id"
      :data-testid="`notification-${index + 1}`"
      class="box notification"
      :class="notificationClass(notification)"
      :role="notificationRole(notification)"
      @mouseenter="notificationService.pause(notification.id)"
      @mouseleave="notificationService.resume(notification.id)"
      @focusin="notificationService.pause(notification.id)"
      @focusout="notificationService.resume(notification.id)"
    >
      <div class="notification-body">
        <component
          :is="notificationIcon(notification)"
          class="notification-icon"
          :size="24"
          weight="bold"
        />
        <span class="notification-text">
          <strong v-if="notification.title" class="notification-title">{{ notification.title }}</strong>
          {{ notification.text }}
          <component
            :is="notification.component.component"
            v-if="notification.component"
            v-bind="notification.component.props"
            :notification="notification"
          />
        </span>
        <div class="notification-controls">
          <span
            v-if="notification.count > 1"
            class="notification-count"
            :data-testid="`notification-${index + 1}-count`"
            aria-hidden="true"
          >
            ×{{ notification.count }}
          </span>
          <button
            class="delete"
            :aria-label="t('notifications.close')"
            @click="notificationService.close(notification.id)"
          ></button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'bulma/sass/utilities/initial-variables' as *;

.notifications {
  z-index: 100;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin-top: 4em;
  padding: 1em;
  display: flex;
  flex-direction: column;
  justify-content: end;
  align-items: end;
  gap: 1em;
  pointer-events: none;
  touch-action: none;

  @media screen and (max-width: $desktop) {
    justify-content: start;
  }

  .notification {
    --notification-alert-color: hsl(
      var(--bulma-notification-h),
      var(--bulma-notification-s),
      var(--bulma-notification-background-l)
    );

    pointer-events: all;
    z-index: 101;
    max-width: 50%;
    padding: 1.25rem;
    background-color: white;
    border: 2px solid var(--notification-alert-color);
    border-radius: 1rem;
    color: var(--bulma-text-strong);

    @media screen and (max-width: $desktop) {
      max-width: 100%;
    }
  }

  .notification-controls {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .notification-count {
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--notification-alert-color);
    line-height: 1;
  }

  .notification-body {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .notification-icon {
    color: var(--notification-alert-color);
    flex-shrink: 0;
    margin-top: 1px;
  }

  .notification-text {
    flex: 1;
    min-width: 0;
  }

  .notification-title {
    display: block;
    margin-bottom: 0.25rem;
  }
}
</style>
